import React, { useState, useRef } from 'react';

/**
 * HoverZoomImage
 * ----------------
 * Displays a product image with a lens-style hover-to-zoom on desktop and a
 * simple scale-on-touch fallback on mobile. Automatically hints the browser
 * for a web-optimized variant when the source is a hosted image (Unsplash,
 * Pexels, Cloudinary, etc.) that supports width query params.
 *
 * Props:
 *   src       – image URL or data URL (required)
 *   alt       – accessible description
 *   className – forwarded to the wrapper
 *   zoom      – zoom factor when the lens is active (default 2)
 *   lensSize  – lens diameter in px (default 140)
 *   testId    – forwarded as data-testid
 */
export const HoverZoomImage = ({
  src, alt = '', className = '', zoom = 2, lensSize = 140, testId,
}) => {
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });   // lens center in %
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const wrapRef = useRef(null);

  const onMouseMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    setDims({ w: rect.width, h: rect.height });
  };

  const enter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDims({ w: rect.width, h: rect.height });
    setHover(true);
  };

  return (
    <div
      ref={wrapRef}
      onMouseEnter={enter}
      onMouseLeave={() => setHover(false)}
      onMouseMove={onMouseMove}
      className={`relative overflow-hidden select-none cursor-zoom-in ${className}`}
      data-testid={testId || 'hover-zoom-image'}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-auto block transition-transform duration-300"
        draggable={false}
      />
      {hover && dims.w > 0 && (
        <div
          className="pointer-events-none absolute rounded-full ring-2 ring-white/80 shadow-2xl hidden md:block"
          style={{
            width: lensSize,
            height: lensSize,
            left: `calc(${pos.x}% - ${lensSize / 2}px)`,
            top: `calc(${pos.y}% - ${lensSize / 2}px)`,
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${dims.w * zoom}px ${dims.h * zoom}px`,
            backgroundPosition: `${pos.x}% ${pos.y}%`,
          }}
          data-testid="hover-zoom-lens"
        />
      )}
    </div>
  );
};

export default HoverZoomImage;
